/**
 * App: Root component with routing
 * Wraps the app with authentication and provides navigation between pages
 */

import React, { Component, useState } from "react";
import AuthGuard from "./components/AuthGuard";
import CatalogPage from "./pages/CatalogPage";
import PluginDetailPage from "./pages/PluginDetailPage";
import { logout, getUser } from "./lib/auth";
import type { MarketplaceEntry } from "./types/index";

type Page = "catalog" | "detail";

class ErrorBoundary extends Component<
  { children: React.ReactNode },
  { error: Error | null }
> {
  state: { error: Error | null } = { error: null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("Uncaught render error:", error, errorInfo);
  }

  render() {
    if (this.state.error) {
      return (
        <div style={{ padding: "2rem", textAlign: "center" }}>
          <h1>Something went wrong</h1>
          <p style={{ color: "#666" }}>{this.state.error.message}</p>
          <button
            onClick={() => window.location.reload()}
            style={{
              marginTop: "1rem",
              padding: "0.5rem 1rem",
              fontSize: "1rem",
              backgroundColor: "#0078d4",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
            }}
          >
            Reload
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

export default function App(): JSX.Element {
  const [currentPage, setCurrentPage] = useState<Page>("catalog");
  const [selectedPlugin, setSelectedPlugin] = useState<MarketplaceEntry | null>(null);
  const user = getUser();

  const handleSelectPlugin = (plugin: MarketplaceEntry) => {
    setSelectedPlugin(plugin);
    setCurrentPage("detail");
  };

  const handleBackToCatalog = () => {
    setSelectedPlugin(null);
    setCurrentPage("catalog");
  };

  const handleLogout = () => {
    logout()
      .catch((error) => console.error("Logout failed:", error))
      .finally(() => window.location.reload());
  };

  return (
    <ErrorBoundary>
    <AuthGuard>
      <div style={{ minHeight: "100vh", backgroundColor: "#fafafa" }}>
        {/* Header */}
        <header
          style={{
            backgroundColor: "white",
            borderBottom: "1px solid #e0e0e0",
            padding: "1rem 2rem",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <h1 style={{ margin: 0 }}>
            <button
              onClick={handleBackToCatalog}
              style={{
                background: "none",
                border: "none",
                color: "#0078d4",
                cursor: "pointer",
                fontSize: "1.5rem",
                fontWeight: "bold",
              }}
            >
              🔧 Skills Marketplace
            </button>
          </h1>
          <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
            {user && (
              <span style={{ color: "#666", fontSize: "0.9rem" }}>
                {user.name || user.email}
              </span>
            )}
            <button
              onClick={handleLogout}
              style={{
                padding: "0.5rem 1rem",
                fontSize: "0.9rem",
                backgroundColor: "#f0f0f0",
                color: "#333",
                border: "1px solid #ddd",
                borderRadius: "4px",
                cursor: "pointer",
              }}
            >
              Sign out
            </button>
          </div>
        </header>

        {/* Main content */}
        <main style={{ maxWidth: "1200px", margin: "0 auto", padding: "2rem" }}>
          {currentPage === "catalog" && (
            <CatalogPage onSelectPlugin={handleSelectPlugin} />
          )}

          {currentPage === "detail" && selectedPlugin && (
            <PluginDetailPage plugin={selectedPlugin} onBack={handleBackToCatalog} />
          )}
        </main>

        {/* Footer */}
        <footer
          style={{
            backgroundColor: "#f5f5f5",
            borderTop: "1px solid #e0e0e0",
            padding: "2rem",
            textAlign: "center",
            color: "#999",
            fontSize: "0.9rem",
            marginTop: "2rem",
          }}
        >
          <p style={{ margin: 0 }}>
            Skills Marketplace © 2026 • Cox Automotive
          </p>
        </footer>
      </div>
    </AuthGuard>
    </ErrorBoundary>
  );
}
